// The record steps 2 to 6 in the Record's words (spec C7, C8, the Record board): step names, the
// one-line why, the stamps, the bank outcomes, the toasts and the reasons a tick was refused. Pure
// functions, so the cards and the tests agree.
import type {
  BankOutcome,
  ContinueAtStep,
  PreviouslyStep,
  StepKey,
  StepsView,
  StepView,
} from '../auth/api';
import { stampTime } from './fetch-lines';
import { parseDate, showValue } from './fix-lines';
import { listDate, money, nyToday } from './renewal-lines';

/** Step names, everywhere (spec C7): 2 is Check AMS360 (Path A) or Renew in AMS360 (Paths B, C). */
export function stepTitle(key: StepKey, amsStep: StepsView['amsStep']): string {
  switch (key) {
    case 'ams360':
      return amsStep === 'check' ? 'Check AMS360' : 'Renew in AMS360';
    case 'email':
      return 'Email insured';
    case 'activity':
      return 'Log activity';
    case 'bank':
      return 'Proof to bank';
    case 'done':
      return 'Done';
  }
}

/**
 * Proof to bank is hidden, not mentioned at all, when there is no mortgagee (Yehuda, Oct 29): the
 * server skips it (no mortgagee on the dec page, or auto), or the VA chose "Not needed" and the
 * renewal is closed. Until Done is ticked a "Not needed" stays as one folded line, so its Undo is
 * still there (only the last step ticked can be undone).
 */
export function bankHidden(v: StepsView): boolean {
  const b = v.steps.bank;
  if (b.state === 'skipped') return true;
  return b.state === 'done' && b.details.outcome === 'not_needed' && v.steps.done.state === 'done';
}

const stepOrder: StepKey[] = ['ams360', 'email', 'activity', 'bank', 'done'];

/** Steps 2 and up as the Record shows them: Proof to bank only when it is not hidden. */
export const shownSteps = (v: StepsView): StepKey[] =>
  stepOrder.filter((k) => k !== 'bank' || !bankHidden(v));

/** A step's number as shown: Done is 5 when Proof to bank is hidden, else 6. */
export const stepNumberOf = (v: StepsView, k: StepKey) => shownSteps(v).indexOf(k) + 2;

/** How many steps the record shows: 5 or 6. */
export const stepCount = (v: StepsView) => shownSteps(v).length + 1;

/** The one-line "Why this step" (spec C8; editable in Settings later). */
export function stepWhy(key: StepKey, amsStep: StepsView['amsStep']): string {
  switch (key) {
    case 'ams360':
      return amsStep === 'check'
        ? 'AMS360 has the renewed term: check its premium against the dec page.'
        : 'AMS360 does not have the renewed term: renew it there with the dec page values.';
    case 'email':
      return 'Send the renewal from your Outlook.';
    case 'activity':
      return 'Log the renewal in AMS360 with the email dragged in.';
    case 'bank':
      return 'Send the renewal to the mortgagee as proof of insurance.';
    case 'done':
      return 'Close the renewal and set the sheet comment.';
  }
}

/** "Opens when step 3 is done", numbered as shown. */
/** The renewal is closed (Done or Cancelled): its Record is read only until it is reopened (spec C16). */
export const isClosedView = (v: StepsView) =>
  v.close.renewalStatus === 'done' || v.close.renewalStatus === 'cancelled';

/** A step on a closed renewal (spec C16), step 1 included. */
export const closedLockText = 'The renewal is closed: Reopen it to work this step';

export const lockedLine = (v: StepsView, key: StepKey) =>
  v.steps.done.state === 'done'
    ? closedLockText
    : key === 'ams360'
      ? 'Opens when the renewal dec page is attached'
      : `Opens when step ${stepNumberOf(v, key) - 1} is done`;

/** "Val Vee, Oct 26, 14:20" */
export const stampLine = (s: StepView['stamp']) =>
  s ? `${s.by ?? 'Someone'}, ${stampTime(s.at)}` : '';

/** A step marked done previously (S16): who did it and the day, no time. "Done previously: Val Vee, Oct 1" */
export const previouslyStamp = (by: string | null, ymd: string) =>
  `Done previously: ${by ?? 'Someone'}, ${listDate(ymd)}`;

/** Whether a step was marked done previously instead of ticked on its card. */
export const isPreviously = (s: StepView) =>
  !!s.details.previously && typeof s.details.previously === 'object';

/**
 * A step the AE stamped on a return (S15 batch 6b; the Record board): "Skipped, returned by Dana
 * Wolfe at Email insured". Only steps before Continue at are stamped, so it is Email insured or later
 * and its name never depends on step 2's path.
 */
export function skippedOnReturn(s: StepView): string | null {
  const m = s.details.skippedOnReturn as { at?: unknown; rewritten?: unknown } | undefined;
  if (!m || typeof m !== 'object' || typeof m.at !== 'string' || m.at === 'dec_page') return null;
  // Rewritten with another carrier and returned for the bank step (S15 batch 6c, Record board).
  if (m.rewritten === true)
    return `Done by ${s.stamp?.by ?? 'Someone'}, rewritten with another carrier`;
  return `Skipped, returned by ${s.stamp?.by ?? 'Someone'} at ${stepTitle(m.at as StepKey, 'check')}`;
}

/** A done step's stamp: who and when it was ticked, or who did it and the day when done previously. */
export const stepStamp = (s: StepView) =>
  skippedOnReturn(s) ??
  (s.stamp && isPreviously(s)
    ? previouslyStamp(s.stamp.by, nyToday(new Date(s.stamp.at)))
    : stampLine(s.stamp));

/** Continue at's step name: "Dec page", or the step's own. */
export const continueAtTitle = (k: ContinueAtStep, amsStep: StepsView['amsStep']) =>
  k === 'dec_page' ? 'Dec page' : stepTitle(k, amsStep);

export type ContinueAtOption = {
  key: ContinueAtStep;
  /** "3. Email insured", with "(done)" on the steps before where it stopped. */
  label: string;
  /** The step's name alone, for the confirm, the toast and Copy for AMS360. */
  title: string;
  disabled: boolean;
  hint: string | null;
};

/**
 * Finish handoff's Continue at (the Record board): step 1 and the steps the Record shows, the ones
 * before where it stopped disabled with "(done)", and where it stopped first picked. The server's
 * rules (returnStamps): with no dec page only step 1; never past a Proof to bank waiting on the
 * insured, since the AE stamps the steps in between.
 */
export function continueAtOptions(v: StepsView): {
  options: ContinueAtOption[];
  stopped: ContinueAtStep;
} {
  const keys: ContinueAtStep[] = ['dec_page', ...shownSteps(v)];
  const settled = (k: ContinueAtStep) =>
    k === 'dec_page' ? decDone(v) : v.steps[k].state === 'done' || v.steps[k].state === 'skipped';
  const stopped = keys.find((k) => !settled(k)) ?? 'done';
  const from = keys.indexOf(stopped);
  const waits = keys.findIndex(
    (k, i) => i >= from && k !== 'dec_page' && v.steps[k].state === 'waiting',
  );
  const options = keys.map((key, i) => {
    const title = continueAtTitle(key, v.amsStep);
    const n = key === 'dec_page' ? 1 : stepNumberOf(v, key);
    const done = i < from;
    const hint =
      stopped === 'dec_page' && i > from
        ? 'Needs a dec page first'
        : waits >= 0 && i > waits
          ? 'Proof to bank waits on the insured'
          : null;
    return {
      key,
      label: `${n}. ${title}${done ? ' (done)' : ''}`,
      title,
      disabled: done || !!hint,
      hint,
    };
  });
  return { options, stopped };
}

/** Step 1 is done: a dec page attached, or marked done previously. */
export const decDone = (v: StepsView) => v.decPage.attached || !!v.decPage.previously;

/** "Step 3", "Steps 3 and 5", "Steps 1, 3 and 5". */
export function stepsText(numbers: number[]): string {
  if (numbers.length === 1) return `Step ${numbers[0]}`;
  return `Steps ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
}

/**
 * The steps still open when Close renewal is pressed, by the number shown: Close anyway when there
 * are any (S16). Done itself is not counted; the server counts them the same way.
 */
export const missingOf = (v: StepsView): number[] => [
  ...(decDone(v) ? [] : [1]),
  ...shownSteps(v)
    .filter((k) => k !== 'done' && v.steps[k].state !== 'done')
    .map((k) => stepNumberOf(v, k)),
];

/** Close renewal's note when steps are still open. */
export const missingNote = (missing: number[]) =>
  `${stepsText(missing)} ${missing.length === 1 ? 'is' : 'are'} not done. Give the reason to close anyway.`;

/** The Close anyway confirm (kit ConfirmPopup: the question with the name, one line on what happens). */
export const closeAnywayTitle = (insured: string) => `Close ${insured} anyway?`;
export const closeAnywayLine = (missing: number[]) =>
  `${stepsText(missing)} ${missing.length === 1 ? 'stays' : 'stay'} unticked. Reopen brings it back.`;

/** One line of Mark steps done previously: a step, and why it cannot be picked when it cannot. */
export type PreviouslyRow = {
  key: PreviouslyStep;
  n: number;
  title: string;
  /** Already done: its stamp. */
  done: string | null;
  /** Neither done nor open to mark (Proof to bank waiting on the insured): why. */
  why: string | null;
};

/** Steps 1 to 5 as the Record shows them (Proof to bank only when shown), for Mark steps done previously. */
export function previouslyRows(v: StepsView): PreviouslyRow[] {
  const dec = v.decPage.attached
    ? 'Dec page attached'
    : v.decPage.previously
      ? previouslyStamp(v.decPage.previously.by, v.decPage.previously.date)
      : null;
  const rows: PreviouslyRow[] = [{ key: 'dec', n: 1, title: 'Dec page', done: dec, why: null }];
  for (const k of shownSteps(v)) {
    if (k === 'done') continue;
    const s = v.steps[k];
    rows.push({
      key: k,
      n: stepNumberOf(v, k),
      title: stepTitle(k, v.amsStep),
      done: s.state === 'done' ? stepStamp(s) || 'Done' : null,
      why:
        s.state === 'waiting'
          ? 'Waiting on the insured for the bank. Finish it on its card.'
          : null,
    });
  }
  return rows;
}

/** Nothing left to mark: every step 1 to 5 is done or waiting on the insured. */
export const nothingToMark = (v: StepsView) =>
  previouslyRows(v).every((r) => r.done !== null || r.why !== null);

/**
 * Done in Mark steps done previously: something picked, and every step before the last one picked
 * is done or picked too, so marking never leaves a gap.
 */
export function previouslyReady(rows: PreviouslyRow[], picked: ReadonlySet<PreviouslyStep>) {
  const last = rows.reduce((at, r, i) => (picked.has(r.key) ? i : at), -1);
  if (last < 0) return false;
  return rows.slice(0, last).every((r) => r.done !== null || picked.has(r.key));
}

/** The toast after Mark steps done previously, with Undo. */
export const previouslyToast = (numbers: number[]) =>
  `${stepsText(numbers)} marked done previously.`;
export const previouslyUndoneToast = 'Undone. The steps are open again.';

/** Why the checkbox of a done step cannot be unticked, or null when it can. */
export function undoTip(view: StepsView, key: StepKey): string | null {
  if (!view.canWork) return 'Only the person handling this renewal or the Manager can change it.';
  if (view.last !== key) return 'Only the last step done can be unticked.';
  if (!view.canUndo) {
    return 'Ticked on another day or by someone else: the Manager can untick it.';
  }
  return null;
}

export const outcomeLabels: Record<BankOutcome, { title: string; sub: string }> = {
  mycoverageinfo: {
    title: 'Submitted on MyCoverageInfo',
    sub: 'The bank takes proof of insurance on its MyCoverageInfo page.',
  },
  email: { title: 'Sent by email', sub: 'Emailed the dec page to the bank.' },
  fax: { title: 'Sent by fax', sub: 'Faxed the dec page to the bank.' },
  loan_not_found: {
    title: 'Loan not found, asked the insured',
    sub: 'The bank does not know the loan. The step waits for the insured to name the new bank.',
  },
  not_needed: { title: 'Not needed', sub: 'Give the reason, for example the loan was paid off.' },
};

/** The outcome card's line: Umbrella, Flood and Agency Billed still send an invoice. */
export function outcomeSub(o: BankOutcome, sendsInvoice: boolean): string {
  if (sendsInvoice && o === 'email') return 'Emailed the dec page and the invoice to the bank.';
  if (sendsInvoice && o === 'fax') return 'Faxed the dec page and the invoice to the bank.';
  return outcomeLabels[o].sub;
}

/**
 * What goes to the bank (Rachy, Oct 28): the dec page only, escrowed or not, since the bank takes
 * the premium from it. Umbrella and Flood (until part B) and Agency Billed keep the old rule.
 */
export const bankFilesNote = (sendsInvoice: boolean) =>
  sendsInvoice
    ? 'Escrowed: the bank pays, so send the dec page and the invoice. Not escrowed: the dec page only, as proof of insurance.'
    : 'Escrowed or not, the bank gets the dec page only. It shows the premium.';

export const bankOutcomeOrder: BankOutcome[] = [
  'mycoverageinfo',
  'email',
  'fax',
  'loan_not_found',
  'not_needed',
];

/** MyCoverageInfo's Agent Search page (loan number, property zip, borrower last name). */
export const myCoverageInfoUrl = 'https://www.mycoverageinfo.com/agent';

/** The Done step's outcome: how the renewal was closed, from Rachy's renewal sheet included. */
export function closedLine(details: Record<string, unknown>): string {
  if (details.source === 'sheet') return 'Closed from renewal sheet';
  // Closed by the AE through Finish handoff, Close it myself (S15 batch 6c): how it ended.
  if (details.source === 'handoff') {
    const as = details.status === 'cancelled' ? 'Cancelled' : 'Done';
    const said = (v: unknown) => (typeof v === 'string' && v ? v : null);
    switch (details.outcome) {
      case 'rewritten': {
        const parts = [
          said(details.carrier) ? `Rewritten to ${String(details.carrier)}` : 'Rewritten',
          said(details.policyNumber) ? `new policy # ${String(details.policyNumber)}` : null,
          typeof details.premium === 'number' ? money(details.premium) : null,
        ];
        return `Closed as ${as}. ${parts.filter(Boolean).join(', ')}`;
      }
      case 'lost': {
        const why = lostWords[String(details.lostReason)];
        const to = said(details.wentTo);
        return `Closed as ${as}. ${['Lost', why, to ? `went to ${to}` : null].filter(Boolean).join(', ')}`;
      }
      case 'not_renewed':
        return `Closed as ${as}. Not renewed${said(details.reason) ? `: ${String(details.reason)}` : ''}`;
      default:
        return `Closed as ${as}. Renewed`;
    }
  }
  const as = details.status === 'need_payment' ? 'Need payment' : 'Done';
  // Closed with steps still open (S16): which ones stayed unticked.
  const open = (details.anyway as { open?: unknown } | undefined)?.open;
  if (Array.isArray(open) && open.length && open.every((n) => typeof n === 'number')) {
    return `Closed anyway as ${as}. ${stepsText(open)} left unticked.`;
  }
  return `Closed as ${as}`;
}

const lostWords: Record<string, string> = {
  price: 'price',
  coverage: 'coverage',
  service: 'service',
  no_response: 'no response',
  other: 'other',
};

/** The reason a renewal was closed anyway, or null. */
export const anywayReason = (details: Record<string, unknown>): string | null => {
  const reason = (details.anyway as { reason?: unknown } | undefined)?.reason;
  return typeof reason === 'string' && reason ? reason : null;
};

/** "Sent by email" with its date or reason, for the done stamp. */
export function outcomeLine(details: Record<string, unknown>): string {
  const o = details.outcome as BankOutcome | undefined;
  if (!o || !(o in outcomeLabels)) return '';
  const label = outcomeLabels[o].title;
  if (o === 'mycoverageinfo' && typeof details.date === 'string') {
    const [y, m, d] = details.date.split('-');
    return `${label} on ${m}/${d}/${y}`;
  }
  if (o === 'not_needed' && typeof details.reason === 'string')
    return `${label}: ${details.reason}`;
  return label;
}

/** The toast after a tick (past tense, spec C7). */
export function tickToast(key: StepKey, amsStep: StepsView['amsStep'], view?: StepsView): string {
  if (key === 'done') {
    const status = view?.close.renewalStatus === 'need_payment' ? 'Need payment' : 'Done';
    return `Renewal closed. Status: ${status}.`;
  }
  if (key === 'bank' && view?.steps.bank.state === 'waiting') {
    return 'Proof to bank waits on the insured for the bank.';
  }
  return `${stepTitle(key, amsStep)} done.`;
}

/** Close renewal's note on a pay-first renewal: the carrier, or the line for Umbrella and Flood. */
export const payFirstNote = (name: string) =>
  `${name} renews only when paid. The record stays in Waiting until you mark Premium received.`;

export const undoToast = (key: StepKey, amsStep: StepsView['amsStep']) =>
  key === 'done' ? 'Renewal reopened.' : `${stepTitle(key, amsStep)} unticked.`;

/** Why the server refused a tick, in the Record's words. */
export function refusalText(error: string): string {
  switch (error) {
    case 'checks_open':
      return 'Confirm the fields marked Check first, under Prior term vs renewal.';
    case 'reading_pending':
      return 'The dec page is still being read. Try again in a moment.';
    case 'no_dec_premium':
      return 'Enter the renewal premium from the dec page first.';
    case 'premium_differs':
      return 'The premiums differ now. Pick one way out.';
    case 'step_locked':
      return 'Finish the step before this one first.';
    case 'too_soon':
      return 'Read from AMS360 a moment ago. Try again in 30 seconds.';
    case 'ams360_not_configured':
      return 'AMS360 is not connected here.';
    case 'ams360_failed':
      return 'AMS360 did not answer. Try again in a few minutes.';
    case 'no_ams_premium':
      return "AMS360's premium for the new term is not in yet. Refresh from AMS360.";
    case 'typed_values_missing':
      return 'The dec page could not be read: enter its renewal premium, policy number and term start under Prior term vs renewal first.';
    case 'not_a_dec_page':
      return 'The file on step 1 is not a renewal dec page.';
    case 'not_last':
      return 'Only the last step done can be unticked.';
    case 'undo_not_allowed':
      return 'Only the Manager can untick this one now.';
    case 'ams_not_updated':
      return 'Update the mortgagee in AMS360 first, then tick Updated in AMS360.';
    case 'bad_date':
      return 'Pick the date it was submitted, today or earlier.';
    case 'no_email_sent':
      return 'Mark the email as sent first.';
    case 'cannot_undo':
      return 'Could not undo. It changed since, or 10 minutes went by.';
    case 'reason_required':
      return 'Give the reason first.';
    case 'earlier_steps_open':
      return 'Pick every step before the last one you pick, or tick it on its card first.';
    case 'bad_person':
      return 'Pick yourself or a Renewal VA.';
    case 'bad_steps':
      return 'Pick at least one of steps 1 to 5.';
    case 'renewal_closed':
      return 'The renewal is closed: Reopen it first.';
    case 'not_downloaded':
      return 'AMS360 has no new term yet. Recheck tomorrow, or renew it by hand.';
    case 'not_waiting':
      return 'Step 2 changed since. Look again.';
    case 'not_manual':
      return 'Step 2 is already Check AMS360.';
    default:
      return 'Could not save. Try again.';
  }
}

/** Step 2 when the file on step 1 is not a renewal dec page (spec C8; the non-renewal steps come later). */
export function notDecPageLine(kind: 'non_renewal' | 'other'): string {
  return kind === 'non_renewal'
    ? 'The file on step 1 is a non-renewal notice, not a dec page: the carrier is not renewing this policy. Tell the Account Executive. The non-renewal steps join the portal later. If a renewal dec page comes after all, add it with Upload PDF.'
    : 'The file on step 1 does not look like a renewal dec page. Add the dec page with Upload PDF; this step opens once it is read.';
}

/** Step 2 when AMS360 shows Renewed but its premium for the new term has not come through. */
export const noAmsPremiumLine =
  "AMS360 shows this policy as Renewed, but its premium for the new term has not come through yet. Refresh from AMS360, or check it there and accept without AMS360's premium.";

/** The toast after Refresh from AMS360. */
export function refreshText(found: boolean, view: StepsView): string {
  if (!found) return 'AMS360 has no new term for this policy yet.';
  const p = view.premiums.ams;
  return p === null
    ? 'AMS360 has the new term, but no premium on it yet.'
    : `Read from AMS360: new term premium ${showValue('totalPremium', p)}.`;
}

/** The step 1 line in Close renewal. */
export const decPageSummary = (view: StepsView) =>
  view.decPage.attached
    ? 'Dec page attached'
    : view.decPage.previously
      ? previouslyStamp(view.decPage.previously.by, view.decPage.previously.date)
      : 'No dec page yet';

/** Which of the shown steps are done, in order (step 1 once the dec page is attached; Proof to bank
 * only when it is shown). */
export function doneSteps(v: StepsView): boolean[] {
  // On a branch (S17): its four steps.
  if (v.branch) return v.branch.steps.map((s) => s.state === 'done');
  return [
    // Auto: the ID cards too (Rachy, Oct 28). Or marked done previously (S16).
    (v.decPage.attached && v.decPage.idCards !== false) || !!v.decPage.previously,
    ...shownSteps(v).map((k) => v.steps[k].state === 'done'),
  ];
}

/**
 * The Dec page step's identity: the renewal, whether it is closed and whether this person works it.
 * Close renewal, Reopen and a change of handler (I'll reshop it, Assign to me) change it, so the
 * step starts again from the view read again instead of its own from before.
 */
export const decStepKey = (renewalId: string, fetch: { closed: boolean; canWork?: boolean }) =>
  `${renewalId}:${fetch.closed ? 'closed' : 'open'}:${fetch.canWork ? 'works' : 'reads'}`;

/** The typed day as YYYY-MM-DD, or why it is not a day that can be used. */
export function previouslyDate(
  raw: string,
  today: string,
): { value: string } | { error: string } | null {
  if (!raw.trim()) return null;
  const d = parseDate(raw);
  if (!d) return { error: 'Enter a date like Oct 30, 2026.' };
  if (d > today) return { error: 'Enter today or an earlier day.' };
  return { value: d };
}

/** Step 2 waits for AMS360's download: "Not downloaded yet" (S16 Batch 3, Record board). */
export const waitsForDownload = (v: StepsView) =>
  !!v.download?.waiting && v.steps.ams360.state !== 'done';

/** The amber tag on step 2 and on the row: "Recheck Nov 13". */
export const recheckTag = (ymd: string) => `Recheck ${listDate(ymd)}`;
/** The line in step 2 and the toast once a recheck is set. */
export const recheckLine = (ymd: string) =>
  `Recheck set for ${listDate(ymd)}. The step stays open.`;
export const notDownloadedLine =
  'The new term is not in AMS360 yet. Check again tomorrow, or renew it by hand like Paths B and C.';
export const manualToast = 'Step 2 is now Renew in AMS360.';
export const backToCheckToast = 'Step 2 is Check AMS360 again. It waits for the download.';
const sourceWords = { ams360: 'in AMS360', dec: 'on the dec page' } as const;

/**
 * Proof to bank when AMS360 and the dec page give different last names (Rachy, Nov 1: "we try
 * both"): both, with where each comes from. Null when there is only one.
 */
export function borrowerLine(v: StepsView): string | null {
  const b = v.bank.borrower;
  const first = v.bank.rows.find((r) => r.label === 'Borrower last name')?.value;
  if (!b?.other || !first) return null;
  return `Two last names: ${first} ${sourceWords[b.source]}, ${b.other.name} ${sourceWords[b.other.source]}. Copy for MyCoverageInfo uses ${first}; if the bank finds no loan, try ${b.other.name}.`;
}
